import { useEffect, useRef, useState } from "react";
import Button from "@mui/material/Button";
import { useRecoilValue } from "recoil";

import CustomFolderPermission from "./CustomFolderPermission";
import { useFolderTreeActions } from "../../recoil/actions/folderTree.actions";
import { useLegalEntitiesActions } from "../../recoil/actions/legalEntities.actions";
import { buttonDisabledSelector, entitiesSelector, folderPermissionSelector } from "../../recoil/selectors/legalEntities.selectors";
import { folderTreeSelector, isSiteFolderPermissionDirtySelector } from "../../recoil/selectors/folderTree.selectors";
import { Entity } from "../../api-client-nswag/custompermission-client";
import { SnackbarMessage, useSnackbar } from "../../hooks";
import { DialogBox } from "../CreateSites/DialogBox";
import { DIALOG_MESSAGES } from "../../constants";

interface Props {
  isCsFlow?: boolean;
  isRefreshingFolderPermissions?: boolean;
  onRefreshRequest?: (ctx: { isSiteLevelFolderPermission: boolean; entityId?: number }) => Promise<void> | void;
  handleSaveChanges?: () => void | Promise<void>;
  handleFolderPermissionModalClose?: () => void;
  title?: string;
  btnContinueDisabled?: boolean;
}

const ManageCustomPermissionsFolderPermissionsPage = ({ isCsFlow, isRefreshingFolderPermissions, onRefreshRequest, handleSaveChanges, handleFolderPermissionModalClose, title, btnContinueDisabled }: Props) => {
  const { showSnackbar } = useSnackbar();
  const siteId = Number(sessionStorage.getItem("siteId") ?? 0);
  const { loadFolderTree } = useFolderTreeActions();
  const { updateSiteFolderPermission, revalidateAllEntitiesIsRestricted } = useLegalEntitiesActions();
  const folderPermissions = useRecoilValue(folderPermissionSelector);
  const folderTree = useRecoilValue(folderTreeSelector);
  const entities = useRecoilValue(entitiesSelector);
  const isButtonDisabled = useRecoilValue(buttonDisabledSelector);
  const isSiteFolderPermissionDirty = useRecoilValue(isSiteFolderPermissionDirtySelector);

  const [selectedLegalEntities, setSelectedLegalEntities] = useState<Entity | undefined>(entities?.[0]);
  const [folderTreeData, setFolderTreeData] = useState<any>([]);
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
  const isSiteFolderPermissionDirtyRef = useRef(isSiteFolderPermissionDirty);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        setShowUnsavedChangesModal(false);
      }

      if (document.visibilityState === "visible") {
        setShowUnsavedChangesModal(false);
      }
    };

    const handleBlur = (e: any) => {
      if (document.visibilityState === "hidden") return;
      if (isSiteFolderPermissionDirtyRef.current) {
        setShowUnsavedChangesModal(true);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("blur", handleBlur);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("blur", handleBlur);
    };
  }, []);

  useEffect(() => {
    isSiteFolderPermissionDirtyRef.current = isSiteFolderPermissionDirty;
  }, [isSiteFolderPermissionDirty]);

  useEffect(() => {
    setFolderTreeData(Array.isArray(folderPermissions) ? folderPermissions : []);
  }, [folderPermissions]);

  const handleFolderPermissionClose = () => {
    if (handleFolderPermissionModalClose) {
      handleFolderPermissionModalClose();
    }
  }

  const handleSave = async () => {
    if (handleSaveChanges) {
      try {
        updateSiteFolderPermission(folderTree ?? []);
        await revalidateAllEntitiesIsRestricted();
        await handleSaveChanges();
        if (!isCsFlow) {
          handleFolderPermissionClose();
        }
      } catch (error) {
        showSnackbar({ message: SnackbarMessage.FOLDER_PERMISSION_UPDATE_FAIL, severity: "error" });
      }
    }
  };

  const handleFolderCancel = () => {
    if (isCsFlow) {
      if (isSiteFolderPermissionDirty) {
        setShowUnsavedChangesModal(true)
      }
    } else {
      handleFolderPermissionClose();
    }
  }

  const handleUnsavedChanges = () => {
    loadFolderTree(folderTreeData);
    setShowUnsavedChangesModal(false);
  }

  const handleAttemptClose = () => {
    setShowUnsavedChangesModal(false);
  }

  const isSaveDisabled = isButtonDisabled || isRefreshingFolderPermissions || !isSiteFolderPermissionDirty;

  return (
    <section className="hq-page custom-page-shell m-20" style={{ paddingTop: "0px", paddingBottom: "10px" }}>
      <div className="custom-page-actions mcp-folder-page-actions">
        <h2 className="mcp-folder-page-title">{title}</h2>
        {!isCsFlow ? (
          <div className="mcp-folder-page-actions-right">
            <Button variant="outlined" onClick={handleFolderPermissionClose} disabled={isRefreshingFolderPermissions}>
              Close
            </Button>
          </div>
        ) : null}
      </div>

      <CustomFolderPermission
        isSiteLevelFolderPermission={true}
        selectedLegalEntities={selectedLegalEntities}
        setSelectedLegalEntities={setSelectedLegalEntities}
        siteId={siteId}
        onRefreshRequest={onRefreshRequest}
        isRefreshing={isRefreshingFolderPermissions}
        isCsFlow={isCsFlow}
      />

      <div className="flex-end-container">
        <Button onClick={handleFolderCancel} variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={isSaveDisabled}
          variant="contained"
          className={isSaveDisabled ? "mcp-save-button-disabled" : undefined}>
          Save Changes
        </Button>
      </div>

      <DialogBox
        maxWidth={"xs"}
        title={DIALOG_MESSAGES.DISCARD_CHANGES.TITLE}
        btnOpenText={DIALOG_MESSAGES.DISCARD_CHANGES.BTN_YES}
        isDisplayModal={showUnsavedChangesModal}
        handleContinue={handleUnsavedChanges}
        handleClose={handleAttemptClose}
      >
        <div className="mcp-unsaved-dialog-body">
          <span className="mcp-unsaved-dialog-text">
            {DIALOG_MESSAGES.DISCARD_CHANGES.BODY_TEXT}
          </span>
        </div>
      </DialogBox>
    </section>
  );
};

export default ManageCustomPermissionsFolderPermissionsPage;
